import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Block, Document } from '@contentful/rich-text-types';
import type { ContentComponentTranslatedEntities } from '$cms/ContentComponent';
import { extendCmsText } from '$cms/index';

export interface MakeContentfulRichTextRendererOptions extends Options {
  placeholder?: Record<string, string | number>;
}

export function makeContentfulRichTextRendererOptions(
  options?: MakeContentfulRichTextRendererOptions
): Options {
  const { placeholder, renderNode, ...rest } = options || {};
  let { renderText = (text: string) => text } = rest;
  if (placeholder) {
    renderText = (text: string) => extendCmsText(text, placeholder);
  }
  return {
    ...rest,
    renderText,
    renderNode,
  };
}

export const renderRichTextContent = (
  richText: Document,
  options?: MakeContentfulRichTextRendererOptions
): React.ReactNode => documentToReactComponents(richText, makeContentfulRichTextRendererOptions(options));

export const findTextNode = ({ content }: Document | Block): string | undefined => {
  if (!content) {
    return undefined;
  }

  // eslint-disable-next-line
  for (const node of content) {
    if (node.nodeType === 'text') {
      return node.value;
    }
    return findTextNode(node as Block);
  }

  return undefined;
};

export const getRawTranslatedEntities = (translatedEntitiesData: any): any => {
  const translatedEntities = translatedEntitiesData?.reduce((o: any, entity: any) => {
    const key = entity.key.split('.')[1];
    if (!key) {
      return o;
    }
    const matchKeyRecord = key.match(/(\w+)([0-9]+)$/);
    if (matchKeyRecord) {
      const [, k, kNo] = matchKeyRecord;
      return Object.assign(o, { [k]: Object.assign(o[k] ?? {}, { [kNo]: entity }) });
    }
    return Object.assign(o, { [key]: entity });
  }, {} as ContentComponentTranslatedEntities);

  return translatedEntities ?? {};
};

interface ContentNode {
  nodeType: string;
  content: TextNode[] | HyperlinkNode[];
}

interface TextNode {
  value?: string;
  nodeType?: string;
}

interface HyperlinkNode {
  nodeType: string;
  content: TextNode[];
  data: {
    uri: string;
  };
}

interface TransformedItem {
  title: string;
  content: Array<{
    subtitle: string;
    uri: string;
  }>;
}

function getTextFromNode(node: ContentNode): string {
  if ('value' in node.content[0]) {
    return (node.content as TextNode[]).map((textNode) => textNode.value || '').join('');
  }
  return '';
}

export const transformContentfulData = (contentfulData: ContentNode[]): TransformedItem[] => {
  const items: TransformedItem[] = [];
  let currentItem: TransformedItem | null = null;

  contentfulData.forEach((node) => {
    if (node.nodeType === 'heading-2') {
      if (currentItem) {
        items.push(currentItem);
      }
      currentItem = { title: getTextFromNode(node), content: [] };
    } else if (node.nodeType === 'paragraph' && currentItem) {
      const hyperlinkNode = node.content.find((n) => n.nodeType === 'hyperlink') as HyperlinkNode;
      if (hyperlinkNode) {
        const subtitle = hyperlinkNode.content.map((n) => n.value).join('');
        const { uri } = hyperlinkNode.data;
        currentItem.content.push({ subtitle, uri });
      }
    }
  });

  if (currentItem) {
    items.push(currentItem);
  }

  return items;
};
